<template>
    <div class="card mb-3 p-3 calendar-filters-v2" @click.stop>
        <div class="row align-items-end">

            <div class="col-md-2 mb-1 mode-filter-wrapper">
                <label>Modalità</label>
                <div class="mode-toggle" :class="'mode-toggle-' + localMode">
                    <button
                        type="button"
                        class="mode-toggle-btn"
                        :class="{ active: localMode === 'consultazione' }"
                        @click="setMode('consultazione')"
                    >
                        Consultazione
                    </button>
                    <button
                        type="button"
                        class="mode-toggle-btn"
                        :class="{ active: localMode === 'modifica' }"
                        @click="setMode('modifica')"
                    >
                        Modifica
                    </button>
                </div>
            </div>

            <div class="col-md-2 mb-1">
                <label>Da</label>
                <input
                    type="date"
                    class="form-control"
                    v-model="localRange.from"
                    :max="localRange.to"
                >
            </div>

            <div class="col-md-2 mb-1">
                <label>A</label>
                <input
                    type="date"
                    class="form-control"
                    v-model="localRange.to"
                    :min="localRange.from"
                >
            </div>

            <div class="col-md-3 mb-1">
                <label>Giorni</label>
                <div class="weekday-list">
                    <div class="weekday-row">
                        <label v-for="d in giorni.slice(0, 3)" :key="d.val" class="weekday-item mb-0">
                            <input type="checkbox" :value="d.val" v-model="localWeekdays">
                            {{ d.label }}
                        </label>
                    </div>
                    <div class="weekday-row">
                        <label v-for="d in giorni.slice(3, 6)" :key="d.val" class="weekday-item mb-0">
                            <input type="checkbox" :value="d.val" v-model="localWeekdays">
                            {{ d.label }}
                        </label>
                    </div>
                </div>
            </div>

            <div class="col-md-3 mb-1 aule-filter-wrapper">
                <label>Aule</label>

                <button
                    type="button"
                    class="form-control select-control aule-filter-toggle"
                    @click.stop.prevent="toggleAuleDropdown"
                >
                    <span v-if="!localAule.length" class="text-muted">Tutte le aule</span>
                    <span v-else class="aule-chip-list">
                        <span
                            v-for="aula in selectedAuleLabels"
                            :key="aula.id"
                            class="aule-chip"
                        >
                            {{ aula.nome }}
                        </span>
                    </span>
                    <span class="aule-caret">▾</span>
                </button>

                <div v-show="showAuleDropdown" class="aule-dropdown" @click.stop>
                    <div class="aule-dropdown-actions">
                        <button type="button" class="btn btn-xs btn-link p-0" @click="selectAllAule">
                            Tutte
                        </button>
                        <button type="button" class="btn btn-xs btn-link p-0" @click="clearAule">
                            Svuota
                        </button>
                    </div>

                    <label
                        v-for="aula in aule"
                        :key="aula.id"
                        class="aule-option"
                    >
                        <input
                            type="checkbox"
                            :value="aula.id"
                            :checked="isAulaSelected(aula.id)"
                            @change="toggleAula(aula.id)"
                        >
                        <span>{{ aula.nome }}</span>
                    </label>
                </div>
            </div>


        </div>
        <div class="calendar-filter-actions">
            <button
                type="button"
                class="btn btn-sm btn-warning"
                :disabled="diagnosticsLoading"
                @click="$emit('diagnostics')"
            >
                {{ diagnosticsLoading ? 'Controllo in corso...' : 'Controlla calendario' }}
            </button>
        </div>
    </div>
</template>

<script>
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'

dayjs.extend(isoWeek)

export default {
    props: {
        range: {
            type: Object,
            default: () => ({})
        },
        weekdays: {
            type: Array,
            default: () => []
        },
        mode: {
            type: String,
            default: 'consultazione'
        },
        aule: {
            type: Array,
            default: () => []
        },
        diagnosticsLoading: {
            type: Boolean,
            default: false
        }
    },

    data() {
        const now = dayjs()

        const defaultFrom = now.startOf('isoWeek').format('YYYY-MM-DD')
        const defaultTo = now.endOf('isoWeek').format('YYYY-MM-DD')

        return {
            syncingFromParent: false,
            localRange: {
                from: this.range && this.range.from ? this.range.from : defaultFrom,
                to: this.range && this.range.to ? this.range.to : defaultTo
            },
            localWeekdays: this.weekdays && this.weekdays.length
                ? [...this.weekdays]
                : [1, 2, 3, 4, 5],
            localMode: this.mode || 'consultazione',
            localAule: [],
            showAuleDropdown: false,
            giorni: [
                { val: 1, label: 'Lun' },
                { val: 2, label: 'Mar' },
                { val: 3, label: 'Mer' },
                { val: 4, label: 'Gio' },
                { val: 5, label: 'Ven' },
                { val: 6, label: 'Sab' }
            ]
        }
    },

    watch: {
        range: {
            deep: true,
            handler(val) {
                if (!val) return

                const nextRange = {
                    from: val.from || this.localRange.from,
                    to: val.to || this.localRange.to
                }

                if (
                    nextRange.from === this.localRange.from &&
                    nextRange.to === this.localRange.to
                ) {
                    return
                }

                this.syncingFromParent = true
                this.localRange = nextRange

                this.$nextTick(() => {
                    this.syncingFromParent = false
                })
            }
        },

        weekdays(val) {
            if (Array.isArray(val) && val.length) {
                const normalized = this.normalizeWeekdays(val)

                if (JSON.stringify(normalized) === JSON.stringify(this.normalizeWeekdays(this.localWeekdays))) {
                    return
                }

                this.syncingFromParent = true
                this.localWeekdays = normalized

                this.$nextTick(() => {
                    this.syncingFromParent = false
                })
            }
        },

        mode(val) {
            if (val && val !== this.localMode) {
                this.syncingFromParent = true
                this.localMode = val

                this.$nextTick(() => {
                    this.syncingFromParent = false
                })
            }
        },

        localRange: {
            deep: true,
            handler() {
                if (this.syncingFromParent) return
                this.normalizeRange()
                this.emitUpdate()
            }
        },

        localWeekdays() {
            if (this.syncingFromParent) return
            this.emitUpdate()
        },

        localMode() {
            if (this.syncingFromParent) return
            this.$emit('update', {
                range: {
                    from: this.localRange.from,
                    to: this.localRange.to
                },
                weekdays: this.normalizeWeekdays(this.localWeekdays),
                mode: this.localMode,
                aule: this.normalizeAule(this.localAule)
            })
        },

        localAule() {
            if (this.syncingFromParent) return
            this.emitUpdate()
        }
    },

    computed: {
        selectedAuleLabels() {
            const selected = this.normalizeAule(this.localAule)

            return (this.aule || [])
                .filter((aula) => selected.indexOf(parseInt(aula.id, 10)) !== -1)
                .map((aula) => {
                    return {
                        id: aula.id,
                        nome: aula.nome
                    }
                })
        }
    },

    mounted() {
        document.addEventListener('click', this.handleDocumentClick)
    },

    beforeDestroy() {
        document.removeEventListener('click', this.handleDocumentClick)
    },

    methods: {
        handleDocumentClick() {
            this.showAuleDropdown = false
        },
        toggleAuleDropdown() {
            this.showAuleDropdown = !this.showAuleDropdown
        },

        setMode(mode) {
            if (mode !== 'consultazione' && mode !== 'modifica') return
            this.localMode = mode
        },

        isAulaSelected(id) {
            id = parseInt(id, 10)
            return this.normalizeAule(this.localAule).indexOf(id) !== -1
        },

        toggleAula(id) {
            id = parseInt(id, 10)
            if (!id) return

            const current = this.normalizeAule(this.localAule)
            const index = current.indexOf(id)

            if (index >= 0) {
                current.splice(index, 1)
            } else {
                current.push(id)
            }

            this.localAule = current
        },

        clearAule() {
            this.localAule = []
            this.showAuleDropdown = false
        },

        selectAllAule() {
            this.localAule = (this.aule || [])
                .map((aula) => parseInt(aula.id, 10))
                .filter((id) => id > 0)
        },
        normalizeRange: function () {
            if (this.localRange.from && this.localRange.to && this.localRange.from > this.localRange.to) {
                this.localRange.to = this.localRange.from
            }
        },
        emitUpdate() {
            this.$emit('update', {
                range: {
                    from: this.localRange.from,
                    to: this.localRange.to
                },
                weekdays: this.normalizeWeekdays(this.localWeekdays),
                mode: this.localMode,
                aule: this.normalizeAule(this.localAule)
            })
        },

        normalizeWeekdays(days) {
            if (!Array.isArray(days)) return []

            return days
                .map((day) => parseInt(day, 10))
                .filter((day) => day >= 1 && day <= 7)
                .filter((day, index, self) => self.indexOf(day) === index)
                .sort((a, b) => a - b)
        },

        normalizeAule(aule) {
            if (!Array.isArray(aule)) return []

            return aule
                .map((id) => parseInt(id, 10))
                .filter((id) => id > 0)
                .filter((id, index, self) => self.indexOf(id) === index)
        }
    }
}
</script>

<style scoped>
.calendar-filters-v2 {
    margin-top: 10px;
    overflow: visible !important;
    position: relative;
    z-index: 30;
}

.calendar-filter-actions {
    display: flex;
    gap: 6px;
    justify-content: flex-end;
    margin-top: 8px;
}

.calendar-filters-v2 label {
    font-weight: 600;
    font-size: 11px;
    margin-bottom: 3px;
}

.mode-filter-wrapper {
    min-width: 220px;
}

.mode-toggle {
    display: flex;
    align-items: center;
    height: 38px;
    padding: 3px;
    border: 1px solid #dbe3ef;
    border-radius: 8px;
    background: #f8fafc;
}

.mode-toggle-btn {
    flex: 1;
    height: 30px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    transition: background-color .15s ease-in-out, color .15s ease-in-out, box-shadow .15s ease-in-out;
}

.mode-toggle-btn.active {
    background: #ffffff;
    color: #0f172a;
    box-shadow: 0 2px 8px rgba(15, 23, 42, .10);
}

.mode-toggle-modifica {
    background: #eff6ff;
    border-color: #93c5fd;
}

.mode-toggle-modifica .mode-toggle-btn.active {
    background: #2563eb;
    color: #ffffff;
}

.mode-toggle-consultazione {
    background: #f8fafc;
    border-color: #dbe3ef;
}

.weekday-list {
    min-height: 38px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 2px;
}

.weekday-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.weekday-item {
    min-width: 42px;
    display: inline-flex;
    align-items: center;
    gap: 3px;
}

.aule-filter-wrapper {
    position: relative;
    z-index: 100;
    overflow: visible;
}

.aule-filter-toggle {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 38px;
    padding-right: 28px;
    overflow: hidden;
    text-align: left;
    background: #fff;
    cursor: pointer;
}

.aule-chip-list {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
}

.aule-chip {
    max-width: 86px;
    display: inline-block;
    padding: 2px 6px;
    border-radius: 999px;
    background: #eef2ff;
    color: #3730a3;
    font-size: 11px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.aule-caret {
    position: absolute;
    right: 9px;
    top: 50%;
    transform: translateY(-50%);
    color: #64748b;
}

.aule-dropdown {
    position: absolute;
    z-index: 9999;
    left: 15px;
    right: 15px;
    top: calc(100% + 2px);
    margin-top: 0;
    max-height: 240px;
    overflow-y: auto;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 6px;
    background: #fff;
    box-shadow: 0 8px 18px rgba(0, 0, 0, .12);
}

.aule-dropdown-actions {
    display: flex;
    justify-content: space-between;
    margin-bottom: 6px;
    padding-bottom: 5px;
    border-bottom: 1px solid #f1f5f9;
}

.aule-option {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 4px 2px;
    margin: 0;
    font-size: 12px;
    cursor: pointer;
}

.aule-option:hover {
    background: #f8fafc;
}

</style>
